<template>
  <div class="role-manage">
    <el-card class="search-card">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="关键字">
          <el-input v-model="searchForm.keyword" placeholder="角色名称/编码" clearable @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择" clearable>
            <el-option label="启用" value="ACTIVE"></el-option>
            <el-option label="停用" value="DISABLED"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch" :loading="searchLoading">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          <el-button @click="handleAdd">新增</el-button>
          <el-button :disabled="selectedRows.length === 0" @click="handleBatchDelete" :loading="batchDeleteLoading">批量删除</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="table-card">
      <el-table :data="tableData" border stripe v-loading="loading" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="50" />
        <el-table-column type="index" label="序号" width="60" :index="indexMethod" />
        <el-table-column prop="roleCode" label="角色编码" width="150" />
        <el-table-column prop="roleName" label="角色名称" width="150" />
        <el-table-column prop="description" label="描述" min-width="200" />
        <el-table-column label="菜单权限" min-width="200">
          <template slot-scope="scope">
            <el-tag v-for="menu in scope.row.menus" :key="menu" size="small" style="margin-right: 4px; margin-bottom: 4px;">
              {{ getMenuName(menu) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'ACTIVE' ? 'success' : 'info'">
              {{ scope.row.status === 'ACTIVE' ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160" />
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" style="color: #F56C6C;" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <div class="pagination-wrapper">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="640px" :close-on-click-modal="false" @open="onDialogOpen">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="角色编码" prop="roleCode">
          <el-input v-model="form.roleCode" placeholder="请输入角色编码" clearable></el-input>
        </el-form-item>
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="form.roleName" placeholder="请输入角色名称" clearable></el-input>
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="2" placeholder="请输入描述"></el-input>
        </el-form-item>
        <el-form-item label="菜单权限" prop="menus">
          <div class="menu-tree-hint">一级菜单可全选其下二级；勾选「ROI管理」一级即含全部 ROI 子页；也可只勾选部分子菜单。</div>
          <el-tree
            ref="menuTree"
            class="menu-perm-tree"
            :data="menuTreeData"
            node-key="key"
            show-checkbox
            default-expand-all
            :props="{ label: 'name', children: 'children' }"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio label="ACTIVE">启用</el-radio>
            <el-radio label="DISABLED">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../services/api';
import {
  TOP_LEVEL_MENU_ITEMS,
  getMenuDisplayName,
  normalizeMenusFromTreeCheckedKeys,
  menusToTreeCheckedKeys
} from '@/utils/menuPermissions';

export default {
  name: 'RoleManage',
  computed: {
    menuTreeData() {
      return TOP_LEVEL_MENU_ITEMS.map((item) => {
        const node = { key: item.key, name: item.name };
        if (item.children && item.children.length) {
          node.children = item.children.map((c) => ({ key: c.key, name: c.name }));
        }
        return node;
      });
    }
  },
  data() {
    return {
      searchForm: {
        keyword: '',
        status: ''
      },
      tableData: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      loading: false,
      searchLoading: false,
      deleteLoading: false,
      batchDeleteLoading: false,
      selectedRows: [],
      dialogVisible: false,
      dialogTitle: '新增角色',
      submitLoading: false,
      editingId: null,
      form: {
        roleCode: '',
        roleName: '',
        description: '',
        menus: [],
        status: 'ACTIVE'
      },
      rules: {
        roleCode: [{ required: true, message: '请输入角色编码', trigger: ['blur', 'change'] }],
        roleName: [{ required: true, message: '请输入角色名称', trigger: ['blur', 'change'] }],
        menus: [{ required: true, message: '请选择菜单权限', trigger: ['blur', 'change'], type: 'array', min: 1 }]
      }
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const res = await api.post('/role/list', {
          keyword: this.searchForm.keyword,
          status: this.searchForm.status,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        if (res.data.success) {
          this.tableData = res.data.data.list;
          this.total = res.data.data.total;
        }
      } catch (error) {
        this.$message.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },
    async handleSearch() {
      this.searchLoading = true;
      this.currentPage = 1;
      await this.loadData();
      this.searchLoading = false;
    },
    resetSearch() {
      this.searchForm = { keyword: '', status: '' };
      this.currentPage = 1;
      this.loadData();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadData();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.loadData();
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    indexMethod(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    getMenuName(key) {
      return getMenuDisplayName(key);
    },
    handleAdd() {
      this.dialogTitle = '新增角色';
      this.editingId = null;
      this.form = { roleCode: '', roleName: '', description: '', menus: [], status: 'ACTIVE' };
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.dialogTitle = '编辑角色';
      this.editingId = row.roleId;
      this.form = {
        roleCode: row.roleCode,
        roleName: row.roleName,
        description: row.description || '',
        menus: row.menus || [],
        status: row.status
      };
      this.dialogVisible = true;
    },
    onDialogOpen() {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
        const tree = this.$refs.menuTree;
        if (tree && typeof tree.setCheckedKeys === 'function') {
          tree.setCheckedKeys(menusToTreeCheckedKeys(this.form.menus || []));
        }
      });
    },
    async handleSubmit() {
      const tree = this.$refs.menuTree;
      if (!tree || typeof tree.getCheckedKeys !== 'function') {
        this.$message.error('菜单树未就绪');
        return;
      }
      const leafKeys = tree.getCheckedKeys(true);
      this.form.menus = normalizeMenusFromTreeCheckedKeys(leafKeys);
      try {
        await this.$refs.form.validate();
      } catch (e) {
        this.$nextTick(() => {
          const errorEl = document.querySelector('.el-form-item.is-error');
          if (errorEl) {
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      this.submitLoading = true;
      try {
        let res;
        if (this.editingId) {
          res = await api.post(`/role/${this.editingId}/update`, this.form);
        } else {
          res = await api.post('/role/add', this.form);
        }
        if (res.data.success) {
          this.$message.success(res.data.message || '操作成功');
          this.dialogVisible = false;
          this.loadData();
        } else {
          this.$message.error(res.data.message || '操作失败');
        }
      } catch (error) {
        this.$message.error(error.response?.data?.message || '操作失败');
      } finally {
        this.submitLoading = false;
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该角色吗？', '提示', { type: 'warning' });
        this.deleteLoading = true;
        const res = await api.post(`/role/${row.roleId}/remove`);
        if (res.data.success) {
          this.$message.success('删除成功');
          this.loadData();
        } else {
          this.$message.error(res.data.message || '删除失败');
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败');
        }
      } finally {
        this.deleteLoading = false;
      }
    },
    async handleBatchDelete() {
      if (this.selectedRows.length === 0) return;
      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedRows.length} 条记录吗？`, '提示', { type: 'warning' });
        this.batchDeleteLoading = true;
        for (const row of this.selectedRows) {
          await api.post(`/role/${row.roleId}/remove`);
        }
        this.$message.success('批量删除成功');
        this.loadData();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('批量删除失败');
        }
      } finally {
        this.batchDeleteLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.role-manage {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 56px;
}

.search-card {
  margin-bottom: 12px;
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.table-card >>> .el-table {
  flex: 1;
}

.menu-tree-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-bottom: 8px;
}

.menu-perm-tree {
  max-height: 360px;
  overflow-y: auto;
  padding: 8px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
}
</style>
